import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import { config } from './config';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Header from './components/Header';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import AdminDashboard from './components/AdminDashboard';
import UserRegistration from './components/UserRegistration';

// Configure Amplify
console.log('🔍 Amplify Configuration:', {
  userPoolId: config.COGNITO_USER_POOL_ID ? `${config.COGNITO_USER_POOL_ID.substring(0, 15)}...` : 'MISSING',
  appClientId: config.COGNITO_APP_CLIENT_ID ? `${config.COGNITO_APP_CLIENT_ID.substring(0, 15)}...` : 'MISSING',
  hasUserPoolId: !!config.COGNITO_USER_POOL_ID,
  hasAppClientId: !!config.COGNITO_APP_CLIENT_ID,
  region: config.AWS_REGION,
});

if (!config.COGNITO_USER_POOL_ID || !config.COGNITO_APP_CLIENT_ID) {
  console.error('❌ CRITICAL: Cognito configuration is missing!', {
    userPoolId: config.COGNITO_USER_POOL_ID,
    appClientId: config.COGNITO_APP_CLIENT_ID,
    AWS_REGION: config.AWS_REGION ? 'SET' : 'MISSING',
    COGNITO_USER_POOL_ID: config.COGNITO_USER_POOL_ID ? 'SET' : 'MISSING',
    COGNITO_APP_CLIENT_ID: config.COGNITO_APP_CLIENT_ID ? 'SET' : 'MISSING',
    COGNITO_DOMAIN: config.COGNITO_DOMAIN ? 'SET' : 'MISSING',
  });
}

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: config.COGNITO_USER_POOL_ID,
      userPoolClientId: config.COGNITO_APP_CLIENT_ID,
      loginWith: {
        email: true,
      },
      signUpVerificationMethod: 'code',
      userAttributes: {
        email: {
          required: true,
        },
        given_name: {
          required: true,
        },
        family_name: {
          required: true,
        },
        phone_number: {
          required: true,
        },
        address: {
          required: false,
        },
      },
      passwordFormat: {
        minLength: 8,
        requireLowercase: true,
        requireUppercase: true,
        requireNumbers: true,
        requireSpecialCharacters: true,
      },
    },
  },
});

const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" />;
};

const AdminRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  // For development, bypass authentication completely
  return children;
};

const AppContent: React.FC = () => {
  const location = useLocation();
  const showHeader = !['/login', '/register'].includes(location.pathname);

  return (
    <div className="App">
      {showHeader && <Header />}
      <Routes>
        <Route path="/" element={<Navigate to="/login" />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              }
            />
            <Route
              path="/register-user"
              element={
                <AdminRoute>
                  <UserRegistration />
                </AdminRoute>
              }
            />
      </Routes>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
};

export default App;
